import { forwardRef, PropsWithoutRef, useImperativeHandle, useState } from 'react';
import { FormTextareaField, createZodFormResolver, lt, useFormControls } from '@mews/framework';
import { Banner, Stack } from '@optimus-web/core';
import { getCurrentEnterpriseDefaultCurrencyCode } from '@mews-commander/legacy-entities/src/selectors/session/enterprise';
import { isBillVirtual } from '@mews-commander/legacy-entities/src/ducks';
import { useCommanderSelector } from '@mews-commander/utils';
import { PaymentFormProps } from '../types';
import { ProcessPaymentFormField } from '../ProcessPaymentFormSchema';
import { useCreateCashPayment } from '../../legacy/CashPayment/queries';
import { PaymentAmountSection } from '../../../common/PaymentAmountSection';
import { PaymentCommonSection } from '../../../common/PaymentCommonSection';
import { useGetPaymentTypesEnabledSettingSuspense } from '../hooks/useGetPaymentTypesEnabledSettingSuspense';
import { ProcessPaymentType } from '../ProcessPaymentType';
import { CashPaymentFormField, CashPaymentFormValues, cashPaymentFormSchema } from './schema';
import { useGetCurrentProfileCashiersSuspense } from './hooks/useGetCurrentProfileCashiersSuspense';
import { CashPaymentCashiersSelect } from './CashPaymentCashiersSelect';
import { useGetInitialFormValues } from './hooks/useGetInitialFormValues';
import { getCurrencyDefaultValue } from './utils';

export const CashPaymentForm = forwardRef(({ accountId, billId, ...rest }: PropsWithoutRef<PaymentFormProps>, ref) => {
    const defaultCurrencyCode = useCommanderSelector<string>(getCurrentEnterpriseDefaultCurrencyCode);
    // @ts-expect-error The selector props type is incorrect
    const isVirtualBill = useCommanderSelector((state) => isBillVirtual(state, { id: billId }));

    const { data, isLoading } = useGetCurrentProfileCashiersSuspense();
    const { mutate } = useCreateCashPayment({ accountId, billId: isVirtualBill ? null : billId ?? null, ...rest });

    const defaultValues = useGetInitialFormValues({ accountId, billId, data });
    const paymentTypesEnabledSetting = useGetPaymentTypesEnabledSettingSuspense();

    const resolver = createZodFormResolver<CashPaymentFormValues>(cashPaymentFormSchema);
    const { renderFormContent, submit, hasDirtyFields, watch, setValue } = useFormControls<CashPaymentFormValues>({
        onSubmit: ({ amount, invoiceIds, currencyCode, notes, cashierId, serviceOrderId, paymentPlanId }) =>
            mutate({
                customerId: accountId,
                amount,
                currencyCode,
                notes,
                cashierId,
                serviceOrderId,
                paymentPlanId,
                invoiceIds,
            }),
        defaultValues,
        resolver,
    });

    const selectedCashierId = watch(CashPaymentFormField.CashierId);
    const selectedCashier = data?.find(({ id }) => id === selectedCashierId);

    const [prevSelectedCashierId, setPrevSelectedCashierId] = useState(selectedCashierId);
    if (prevSelectedCashierId !== selectedCashierId) {
        setPrevSelectedCashierId(selectedCashierId);
        const currencyCodeToSet = getCurrencyDefaultValue(defaultCurrencyCode, selectedCashier) || '';

        setValue(ProcessPaymentFormField.CurrencyCode, currencyCodeToSet);
    }

    const enabledCurrencyCodes =
        (selectedCashier?.enabledCurrencies.map(({ code }) => code).filter((code) => code) as string[]) || [];

    useImperativeHandle(
        ref,
        () => ({
            submit,
            hasDirtyFields,
        }),
        [submit, hasDirtyFields]
    );

    if (!isLoading && paymentTypesEnabledSetting[ProcessPaymentType.CashPayment] === false) {
        return <Banner primaryText={lt('NoCashierOrCurrencyAvailable')} variant="info" />;
    }

    return renderFormContent(
        <Stack spacing="300" vertical>
            <PaymentCommonSection />
            <CashPaymentCashiersSelect />
            <PaymentAmountSection enabledCurrencies={enabledCurrencyCodes} />
            <FormTextareaField name={ProcessPaymentFormField.Notes} label={lt('Notes')} rows={1} />
        </Stack>
    );
});
